import { Link } from 'react-router-dom';

const features = [
  {
    icon: 'ri-file-list-3-line',
    title: 'Smart Templates',
    description: 'Ready-to-use spreadsheet structures for business and personal productivity. Choose from dozens of professionally designed templates that save you hours of setup time and help you get started immediately.',
  },
  {
    icon: 'ri-database-2-line',
    title: 'Data Organization',
    description: 'Manage information efficiently with customizable spreadsheet systems. Sort, filter, and visualize your data with intuitive tools that make complex information easy to understand and act upon.',
  },
  {
    icon: 'ri-flow-chart',
    title: 'Workflow Improvement',
    description: 'Reduce repetitive tasks and improve productivity with automation features. Streamline your daily operations and focus on what matters most for your business growth and success.',
  },
];

const comparisonHeaders = ['Feature', 'VigorBuy Spreadsheet', 'Excel', 'Google Sheets', 'Other Tools'];
const comparisonRows = [
  ['Templates', 'Advanced', 'Medium', 'Basic', 'Different'],
  ['Collaboration', 'Yes', 'Limited', 'Excellent', 'Depends'],
  ['Automation', 'Yes', 'Yes', 'Limited', 'Depends'],
  ['Business Use', 'Excellent', 'Excellent', 'Good', 'Variable'],
  ['Learning Curve', 'Easy', 'Medium', 'Easy', 'Variable'],
];

export default function ContentSection() {
  return (
    <>
      {/* What Is Section */}
      <section className="py-12 md:py-16" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
        <div className="section-container">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-center mb-6 text-foreground-950">
              What Is VigorBuy Spreadsheet?
            </h2>
            <p className="text-sm md:text-base leading-relaxed mb-4 text-foreground-700">
              VigorBuy Spreadsheet is a powerful and intuitive spreadsheet platform built for individuals and businesses who need reliable data management tools. Unlike traditional desktop software, VigorBuy Spreadsheet operates seamlessly in the cloud, giving you access to your data from anywhere at any time. It combines the familiar spreadsheet interface with advanced features for <strong>data organization</strong>, template management, and workflow automation.
            </p>
            <p className="text-sm md:text-base leading-relaxed mb-4 text-foreground-700">
              Whether you are a small business owner tracking inventory, a <strong>financial analyst</strong> building reports, or a team leader managing project timelines, VigorBuy Spreadsheet adapts to your specific use case. The platform supports hundreds of pre-built templates covering everything from basic budgeting to complex <strong>business analytics</strong>, making it accessible to both beginners and advanced users.
            </p>
            <p className="text-sm md:text-base leading-relaxed text-foreground-700">
              What sets VigorBuy Spreadsheet apart is its focus on simplicity without sacrificing capability. Every feature is designed with the user experience in mind, reducing the steep learning curve commonly associated with spreadsheet software. The intuitive interface, combined with comprehensive <strong>spreadsheet tools</strong> and integrations, creates a smooth workflow that helps you accomplish more in less time.
            </p>
          </div>
        </div>
      </section>

      {/* Why People Search Section */}
      <section className="py-12 md:py-16" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
        <div className="section-container">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-center mb-6 text-foreground-950">
              Why People Search for VigorBuy Spreadsheet
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: 'ri-speed-up-line', title: 'Productivity Boost', desc: 'Users seek spreadsheet solutions that genuinely improve efficiency and reduce manual data entry time in their daily operations.' },
                { icon: 'ri-robot-line', title: 'Automation Needs', desc: 'Businesses want to automate repetitive spreadsheet tasks, freeing up valuable time for strategic decision-making and growth planning.' },
                { icon: 'ri-file-copy-line', title: 'Template Access', desc: 'Professionals look for ready-made templates that eliminate the need to build complex spreadsheet structures from scratch every time.' },
                { icon: 'ri-bar-chart-grouped-line', title: 'Better Reporting', desc: 'Teams need comprehensive reporting tools that turn raw data into actionable insights with minimal manual formatting effort.' },
                { icon: 'ri-stack-line', title: 'Data Organization', desc: 'Companies require structured data management systems that keep information organized, searchable, and easy to maintain over time.' },
                { icon: 'ri-shield-check-line', title: 'Security Concerns', desc: 'Organizations prioritize secure spreadsheet solutions that protect sensitive business data while maintaining collaborative flexibility.' },
              ].map((item) => (
                <div key={item.title} className="flex gap-4 p-4 rounded-lg" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'oklch(var(--accent-100))' }}>
                    <i className={`${item.icon} text-lg`} style={{ color: 'oklch(var(--accent-600))' }}></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-sm mb-1 text-foreground-900">{item.title}</h4>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-16" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
        <div className="section-container">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-center mb-2 text-foreground-950">
            VigorBuy Spreadsheet Features
          </h2>
          <p className="text-sm text-center mb-8 text-foreground-600">
            Powerful capabilities designed for modern data management
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {features.map((feat) => (
              <div key={feat.title} className="card-base text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'oklch(var(--primary-100))' }}>
                  <i className={`${feat.icon} text-2xl`} style={{ color: 'oklch(var(--primary-500))' }}></i>
                </div>
                <h4 className="font-heading font-semibold text-base mb-2 text-foreground-950">{feat.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{feat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-12 md:py-16" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
        <div className="section-container">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-center mb-2 text-foreground-950">
            VigorBuy Spreadsheet Comparison
          </h2>
          <p className="text-sm text-center mb-8 text-foreground-600">
            See how VigorBuy Spreadsheet stacks up against other popular tools
          </p>
          <div className="overflow-x-auto max-w-4xl mx-auto rounded-lg" style={{ border: '1px solid oklch(var(--background-300))' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'oklch(var(--primary-100))' }}>
                  {comparisonHeaders.map((h) => (
                    <th key={h} className="px-4 py-3 text-left font-heading font-semibold text-xs md:text-sm whitespace-nowrap text-foreground-900">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, ri) => (
                  <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? 'oklch(var(--background-50))' : 'oklch(var(--background-100))' }}>
                    {row.map((cell, ci) => (
                      <td key={ci} className={`px-4 py-3 text-xs md:text-sm whitespace-nowrap ${ci === 1 ? 'font-semibold' : ''}`}
                        style={{ color: ci === 1 ? 'oklch(var(--primary-600))' : 'oklch(var(--foreground-700))' }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Guide CTA */}
      <section className="py-12 md:py-16" style={{ backgroundColor: 'oklch(var(--accent-100))' }}>
        <div className="section-container text-center">
          <h2 className="font-heading font-semibold text-xl md:text-2xl mb-3 text-foreground-950">
            Want to Learn More?
          </h2>
          <p className="text-sm md:text-base mb-6 max-w-xl mx-auto text-foreground-700">
            Dive deep into our comprehensive VigorBuy Spreadsheet guide covering reviews, comparisons, templates, and expert tips for getting the most out of your spreadsheet tools.
          </p>
          <Link
            to="/vigorbuy-spreadsheet-guide"
            className="btn-primary text-sm px-8 py-3.5 rounded-lg inline-flex"
          >
            <i className="ri-article-line mr-2"></i>
            Read the Full Guide
          </Link>
        </div>
      </section>
    </>
  );
}